'use client';

import type {ObjectTypeField} from '@/matic/features/master-data/types/master-data-types';
import {TextField} from './TextField';
import {MarkdownTextField} from './MarkdownTextField';
import {NumberField} from './NumberField';
import {DropdownField} from './DropdownField';
import {MultiselectField} from './MultiselectField';
import {DateField} from './DateField';
import {BooleanField} from './BooleanField';
import {ObjectReferenceField} from '@/matic/features/_shared/object-reference';
import {LONG_TEXT_FIELD_KEYS} from '@/matic/features/master-data/constants';

type MasterDataFieldRendererProps = {
  field: ObjectTypeField;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  disabled?: boolean;
};

export function MasterDataFieldRenderer({
  field,
  value,
  onChange,
  error,
  disabled,
}: MasterDataFieldRendererProps) {
  const common = {
    value,
    onChange,
    error,
    disabled,
    label: field.label,
    required: field.required,
  };

  switch (field.fieldType) {
    case 'text': {
      if (LONG_TEXT_FIELD_KEYS.has(field.fieldKey)) {
        return <MarkdownTextField {...common} />;
      }

      return <TextField {...common} />;
    }

    case 'number': {
      return (
        <NumberField
          {...common}
          onChange={(v) => {
            onChange(v);
          }}
        />
      );
    }

    case 'dropdown': {
      return (
        <DropdownField
          {...common}
          value={value as string | null | undefined}
          options={field.config?.options ?? []}
        />
      );
    }

    case 'multiselect': {
      return (
        <MultiselectField
          {...common}
          options={field.config?.options ?? []}
          onChange={(v) => {
            onChange(v);
          }}
        />
      );
    }

    case 'date': {
      return <DateField {...common} />;
    }

    case 'boolean': {
      return (
        <BooleanField
          {...common}
          onChange={(v) => {
            onChange(v);
          }}
        />
      );
    }

    case 'object_reference': {
      return (
        <ObjectReferenceField
          referenceObjectTypeId={field.config?.referenceObjectTypeId ?? ''}
          value={value as string | null}
          onChange={(v) => {
            onChange(v);
          }}
          error={error}
          disabled={disabled}
          label={field.label}
          required={field.required}
        />
      );
    }
  }
}
